import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import Chip from "@mui/material/Chip";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { routeConfigs } from "../../../navigation/routeConfig";
import { currencyFormat } from "../../../components/utils/utils";
import { BODY_FONT, cardSx, DISPLAY_FONT, INK, INK_SOFT, LINE, PRIMARY } from "../dashboardTokens";
import { StatusBadge } from "./StatusBadge";

export interface DashboardOrderRow {
  id: string | number;
  orderNumber: string;
  type: "Buy" | "Sell";
  createdAt: string;
  amount: number;
  status?: number;
  shippingStatus?: number;
  paymentStatus?: number;
}

interface RecentOrdersTableProps {
  rows: DashboardOrderRow[];
  loading?: boolean;
}

export const RecentOrdersTable: React.FC<RecentOrdersTableProps> = ({ rows, loading }) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography
            sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 18, color: INK }}
          >
            Recent Activity
          </Typography>
          <Typography sx={{ fontFamily: BODY_FONT, fontSize: 13, color: INK_SOFT, mt: 0.25 }}>
            Latest buy and sell orders
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => navigate(routeConfigs.buyHistory.path)}
          sx={{
            textTransform: "none",
            fontFamily: BODY_FONT,
            fontWeight: 600,
            color: PRIMARY,
          }}
        >
          View all
        </Button>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Order ID", "Type", "Status", "Date", "Amount", ""].map((header) => (
                <TableCell
                  key={header || "action"}
                  sx={{
                    fontFamily: BODY_FONT,
                    fontWeight: 700,
                    fontSize: 12,
                    color: INK_SOFT,
                    borderBottom: `1px solid ${LINE}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={`skel-${idx}`}>
                  {Array.from({ length: 6 }).map((__, cellIdx) => (
                    <TableCell key={cellIdx}>
                      <Skeleton height={22} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", color: INK_SOFT }}>
                  No recent orders yet.
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((row) => (
                <TableRow
                  key={`${row.type}-${row.id}`}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell
                    sx={{
                      fontFamily: BODY_FONT,
                      fontWeight: 600,
                      color: INK,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                  >
                    #{row.orderNumber}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.type}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: row.type === "Buy" ? "rgba(37, 99, 235, 0.08)" : "rgba(46, 125, 50, 0.1)",
                        color: row.type === "Buy" ? PRIMARY : "#2E7D32",
                        borderRadius: "6px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge order={row} />
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: BODY_FONT, fontSize: 13, color: INK_SOFT, whiteSpace: "nowrap" }}
                  >
                    {moment(row.createdAt).format("MMM D, YYYY")}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: BODY_FONT, fontWeight: 600, fontSize: 13, color: INK, whiteSpace: "nowrap" }}
                  >
                    {currencyFormat(row.amount)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(
                          row.type === "Buy"
                            ? routeConfigs.buyHistory.path
                            : routeConfigs.sellHistory.path
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 600,
                        color: PRIMARY,
                        minWidth: 0,
                      }}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RecentOrdersTable;
